// providerMeta.js - single source of truth for per-source labels and
// column-visibility rules used by AdsMatrixView and friends.
//
// Add a source here and the matrix picks it up without per-component
// `source === "google"` branches scattered across the codebase. The
// row data carries the source ("meta" | "google" | …); render code
// looks the label up in the map below.

const FALLBACK = {
  displayName: "Unknown",
  endDateLabel: "End date",
  mediaCtaLabel: "View media",
  mediaCtaTitle: "Open ad media",
  // Which provider-specific columns are RELEVANT for this source.
  // The UI renders them conditionally - a column hidden here means the
  // matrix won't even reserve a slot for that source. (Universal
  // columns like headline/start_date are always shown.)
  showsTopic: false,
  showsImpressionsRange: false,
  showsTotalDaysShown: false,
  showsAudienceFlags: false,
  showsRegionsSummary: false,
  // Whether to embed Google's ATC details link
  showsAtcDetailLink: false,
};

const PROVIDERS = {
  meta: {
    displayName: "Meta",
    endDateLabel: "End date",
    mediaCtaLabel: "View on Facebook",
    mediaCtaTitle: "Open ad in Facebook Ad Library",
    showsTopic: false,
    showsImpressionsRange: false,
    showsTotalDaysShown: false,
    showsAudienceFlags: false,
    showsRegionsSummary: false,
    showsAtcDetailLink: false,
  },
  google: {
    displayName: "Google",
    endDateLabel: "Last seen",
    mediaCtaLabel: "View media",
    mediaCtaTitle: "Open ad media",
    showsTopic: true,
    showsImpressionsRange: true,
    showsTotalDaysShown: true,
    showsAudienceFlags: true,
    showsRegionsSummary: true,
    showsAtcDetailLink: true,
  },
  tiktok: {
    displayName: "TikTok",
    endDateLabel: "Last shown",
    mediaCtaLabel: "View on TikTok",
    mediaCtaTitle: "Open ad in the TikTok Ad Library",
    showsTopic: false,
    showsImpressionsRange: false,
    showsTotalDaysShown: false,
    showsAudienceFlags: false,
    showsRegionsSummary: false,
    showsAtcDetailLink: false,
  },
};

export function providerMeta(source) {
  const key = (source || "").toLowerCase();
  return PROVIDERS[key] || FALLBACK;
}

// Helpers consumed by the matrix to format Google-only fields.

// Compact-format big numbers: 12345 → "12K", 1500000 → "1.5M"
function _compact(n) {
  if (n == null || isNaN(n)) return "-";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  return `${n}`;
}

export function formatImpressionsRange(lower, upper) {
  if (lower == null && upper == null) return null;
  if (lower != null && upper != null && lower !== upper) {
    return `${_compact(lower)}–${_compact(upper)}`;
  }
  return _compact(upper ?? lower);
}

// audience_selection_flags is a dict like {demographic_info: "Included", ...}
// Returns a short label list, ready for chip rendering.
export function audienceFlagLabels(flags) {
  if (!flags || typeof flags !== "object") return [];
  const MAP = {
    demographic_info: "Demographic",
    geographic_locations: "Geographic",
    contextual_signals: "Contextual",
    customer_lists: "Customer lists",
    topics_of_interest: "Topics of interest",
  };
  return Object.entries(flags)
    .filter(([, v]) => v && v !== "Not included")
    .map(([k]) => MAP[k] || k);
}

// region_metrics_json is an array of {code, name, impressions: {lower, upper}, ...}
// Returns a short summary string: top 3 regions by upper bucket.
export function regionsSummary(regions) {
  if (!Array.isArray(regions) || !regions.length) return null;
  const sorted = [...regions]
    .filter((r) => r && typeof r === "object")
    .sort((a, b) => (b?.impressions?.upper || 0) - (a?.impressions?.upper || 0))
    .slice(0, 3);
  return sorted
    .map((r) => {
      const code = r.code || r.region_code || "?";
      const range = formatImpressionsRange(
        r?.impressions?.lower,
        r?.impressions?.upper,
      );
      return range ? `${code} ${range}` : code;
    })
    .join(" · ");
}

// Hosts that serve a creative as a plain image file. Mirrors
// _DIRECT_IMAGE_HOSTS in snapshot_service.py. Google's creative images live on
// these with no file extension, so an extension check alone missed every one
// and the card waited for a screenshot of something the browser can show.
const DIRECT_IMAGE_HOSTS = [
  "tpc.googlesyndication.com",
  "lh3.googleusercontent.com", "lh4.googleusercontent.com",
  "lh5.googleusercontent.com", "lh6.googleusercontent.com",
  "yt3.googleusercontent.com", "yt3.ggpht.com", "i.ytimg.com",
];

export function isDirectImageUrl(url) {
  if (!url) return false;
  if (/\.(png|jpe?g|gif|webp|bmp|tiff?)($|\?)/i.test(url)) return true;
  try {
    const host = new URL(url).hostname.toLowerCase();
    if (host === "s0.2mdn.net" && !url.includes("videoplayback")) return true;
    return DIRECT_IMAGE_HOSTS.some(h => host === h || host.endsWith("." + h));
  } catch {
    return false;
  }
}
